import type { ReactNode } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PatientExternalLinks } from '../../work-queue/tasks/PatientExternalLinks';
import {
    NotificationAssigneeCell,
    type AssignablePatient
} from '../notifications/NotificationAssigneeCell';
import {
    MonitoringPatientIdentity,
    type MonitoringPatientSummary
} from './MonitoringPatientIdentity';
import type { ListFilterSummary } from './listFilterSummary';
import type { ListPositionLabel } from './listPosition';
import { OpenVirtualAppointmentsChip } from './OpenVirtualAppointmentsChip';
import { PatientStepButtons } from './PatientStepButtons';

interface PatientViewHeaderProps {
    patient: MonitoringPatientSummary & AssignablePatient;
    // The view's own counts (levels, messages to review), shown before the open-VA chip.
    badges?: ReactNode;
    // The view's own editable fields, shown beside the assignee.
    controls?: ReactNode;
    backLabel: string;
    onBack: () => void;
    onPrevious?: () => void;
    onNext?: () => void;
    position: ListPositionLabel | null;
    // The list Previous and Next walk, shown on hovering the position.
    listSummary: ListFilterSummary;
    onShowVirtualAppointments: () => void;
}

export function PatientViewHeader({
    patient,
    badges,
    controls,
    backLabel,
    onBack,
    onPrevious,
    onNext,
    position,
    listSummary,
    onShowVirtualAppointments
}: PatientViewHeaderProps) {
    return (
        <header className="flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white px-4 py-3 font-inter">
            <button
                type="button"
                onClick={onBack}
                aria-label={backLabel}
                title={backLabel}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 transition-colors hover:bg-gray-100"
            >
                <ArrowLeftIcon aria-hidden="true" className="h-4 w-4 text-gray-600" />
            </button>
            <MonitoringPatientIdentity patient={patient} size="header" />
            <PatientExternalLinks patientId={patient.patientId} />
            <div className="flex flex-wrap items-center gap-2">
                {badges}
                <OpenVirtualAppointmentsChip
                    patientId={patient.patientId}
                    onClick={onShowVirtualAppointments}
                />
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-4">
                {controls}
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Assignee:</span>
                    <NotificationAssigneeCell row={patient} dropUp={false} align="right" />
                </div>
                <PatientStepButtons
                    onPrevious={onPrevious}
                    onNext={onNext}
                    position={position}
                    listSummary={listSummary}
                />
            </div>
        </header>
    );
}
